import {
  ReactNode,
  Ref,
  RefObject,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { ChevronRight, History } from 'lucide-react';

import {
  CommandGroup,
  CommandMenu,
  CommandMenuHandle,
} from '@/components/ui/command-menu-clean';
import type { PromptContextName, PromptWorkType } from '@/types/prompts';
import { contextFillsFrom, contextNamesByName } from '@/utils/promptContext';

import { PromptGroupIcon } from './PromptGroupIcon';
import { fitPromptPane } from './promptPaneRoom';
import { PromptMenuPreview } from './promptPreview';
import { PromptFill, usePromptFillStep } from './usePromptFillStep';
import { usePromptFillContext } from './usePromptSubject';

export { PromptMenuHeader } from './PromptMenuHeader';

export type PromptMenuRow = {
  key: string;
  label: string;
  /** Muted text before the label: the group word the title starts with. */
  prefix?: string;
  iconUrl?: string;
  lucideIcon?: string;
  /** A group row drills in (chevron); an entry inserts a chip. */
  kind: 'group' | 'entry';
  mine?: boolean;
  /** A Recent row (#596): an entry with the muted history icon. */
  recent?: boolean;
  /** A title match at the top level: its group's icon and, as the trail, its group. */
  groupLabel?: string;
  /** The group's type of work: tints the icon from the agent palette. */
  workType?: PromptWorkType;
  /** Extra words the menu's own search matches (the + picker). */
  keywords?: string;
  /** What the pane beside the menu shows while this row is active. */
  preview?: PromptMenuPreview;
};

export type PromptMenuSection = {
  key: string;
  /** Small uppercase label above the rows; '' for none. */
  label: string;
  rows: PromptMenuRow[];
};

type PromptCommandMenuProps = {
  sections: PromptMenuSection[];
  onPick: (key: string) => void;
  /** What ↵ does, in the footer: "to open" or "to insert". */
  enterHint: string;
  /** Controlled query from the editor: no search row, no own filtering. */
  query?: string;
  header?: ReactNode;
  controlRef?: Ref<CommandMenuHandle>;
  placeholder?: string;
  autoFocus?: boolean;
  onClose?: () => void;
  /** A template's fill step, shown in the list's place (VH3_CONNECT #592). */
  fill?: PromptFill | null;
  /**
   * A menu that opens with its left edge on this element's (the + picker on
   * its button) fits the preview pane to the room left inside the chat. The
   * `/` menu's host fits it as it positions the menu.
   */
  paneAnchor?: RefObject<HTMLElement | null>;
};

const SNIPPET_MAX = 280;

/** The pane beside the menu: the active template in full, on a wide screen. */
function PreviewPane({
  preview,
  contextNames,
}: {
  preview: PromptMenuPreview;
  contextNames: PromptContextName[];
}) {
  const body =
    preview.body.length > SNIPPET_MAX
      ? `${preview.body.slice(0, SNIPPET_MAX).trimEnd()}…`
      : preview.body;
  // Context Variables (spec FSI #1230) read by label, with the chat that
  // fills them.
  const contextByName = useMemo(
    () => contextNamesByName(contextNames),
    [contextNames]
  );
  const fields = preview.variables.filter(
    variable => !contextByName.has(variable.name)
  );
  const known = preview.variables.flatMap(variable => {
    const item = contextByName.get(variable.name);
    return item ? [item] : [];
  });
  return (
    <aside className='prompt-menu__pane' data-cy='prompt-menu-pane'>
      <p className='prompt-menu__pane-title'>{preview.title}</p>
      <p className='prompt-menu__pane-body'>{body}</p>
      {fields.length > 0 && (
        <ul className='prompt-menu__pane-vars' aria-label='Fields to fill'>
          {fields.map(variable => (
            <li key={variable.name} className='prompt-menu__pane-var'>
              <code>{`{${variable.name}}`}</code>
              {variable.description && <span> {variable.description}</span>}
              {!variable.description && variable.default && (
                <span> defaults to “{variable.default}”</span>
              )}
            </li>
          ))}
        </ul>
      )}
      {known.length > 0 && (
        <ul
          className='prompt-menu__pane-vars'
          aria-label='Filled from context'
          data-cy='prompt-menu-pane-context'
        >
          {known.map(item => (
            <li key={item.name} className='prompt-menu__pane-var'>
              <span className='prompt-menu__pane-context-label'>
                {item.label}
              </span>
              <span className='prompt-menu__pane-context-fills'>
                {` · ${contextFillsFrom(item)}`}
              </span>
            </li>
          ))}
        </ul>
      )}
    </aside>
  );
}

/**
 * The `/` and `+` prompt menus on the vendored affectUI Command Menu (clean),
 * on the drawer's tokens (see `.prompt-menu` in index.css). With `fill`, the
 * same card shows a template's fill step instead of the rows. On a wide
 * screen a pane beside the card previews the active template in full.
 */
export function PromptCommandMenu({
  sections,
  onPick,
  enterHint,
  query,
  header,
  controlRef,
  placeholder,
  autoFocus,
  onClose,
  fill,
  paneAnchor,
}: PromptCommandMenuProps) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const fillStep = usePromptFillStep(fill);
  // The chat's own lookup (a chat without a provider asks for the list
  // alone), so the menu adds no request of its own.
  const contextNames = usePromptFillContext().names;
  const [activeKey, setActiveKey] = useState<string | null>(null);

  const groups = useMemo<CommandGroup[]>(
    () =>
      sections.map(section => ({
        id: section.key,
        label: section.label,
        commands: section.rows.map(row => ({
          id: row.key,
          label: row.label,
          prefix: row.prefix,
          keywords: row.keywords,
          // Group rows carry the icon, and so does a title match at the top
          // level (its group's); inside a drilled-in group the header already
          // shows it, so entry rows stay a plain list of titles.
          icon:
            row.kind === 'group' || row.groupLabel ? (
              <PromptGroupIcon
                lucideIcon={row.lucideIcon}
                iconUrl={row.iconUrl}
                workType={row.workType}
              />
            ) : row.recent ? (
              <History
                size={16}
                strokeWidth={1.5}
                className='prompt-menu__recent-icon'
                data-cy='prompt-recent-icon'
                aria-hidden
              />
            ) : undefined,
          trail:
            row.kind === 'group' ? (
              <ChevronRight
                size={14}
                className='prompt-menu__chevron'
                aria-hidden
              />
            ) : row.groupLabel || row.mine ? (
              <span className='prompt-menu__trail'>
                {row.groupLabel && (
                  <span
                    className='prompt-menu__mine'
                    data-cy='prompt-entry-group'
                  >
                    {row.groupLabel}
                  </span>
                )}
                {row.mine && (
                  <span className='prompt-menu__mine' data-cy='prompt-mine'>
                    Mine
                  </span>
                )}
              </span>
            ) : undefined,
        })),
      })),
    [sections]
  );

  const preview = useMemo(() => {
    if (fillStep || !activeKey) return undefined;
    for (const section of sections) {
      const row = section.rows.find(candidate => candidate.key === activeKey);
      if (row) return row.preview;
    }
    return undefined;
  }, [activeKey, fillStep, sections]);

  // The pane beside the card stays inside the chat. The card's place comes
  // from the anchor, not its own box, which its popover may not have
  // positioned yet: its left edge on the anchor's, shifted to stay on screen.
  const showsPane = !!preview;
  useLayoutEffect(() => {
    const menu = wrapperRef.current;
    if (!showsPane || !menu || !paneAnchor) return;
    const fit = () => {
      const anchor = paneAnchor.current;
      if (!anchor) return;
      const width = menu.offsetWidth;
      const left = Math.min(
        anchor.getBoundingClientRect().left,
        window.innerWidth - 8 - width
      );
      fitPromptPane(menu, anchor, left + width);
    };
    fit();
    window.addEventListener('resize', fit);
    return () => window.removeEventListener('resize', fit);
  }, [showsPane, paneAnchor]);

  // Back from the fill step in the + picker: the search field (with its
  // text) takes focus again, as when the menu opened.
  const hadFill = useRef(false);
  useEffect(() => {
    const hasFill = !!fillStep;
    if (hadFill.current && !hasFill && query === undefined && autoFocus) {
      wrapperRef.current
        ?.querySelector<HTMLInputElement>('.ui-cmdc__search input')
        ?.focus();
    }
    hadFill.current = hasFill;
  }, [fillStep, query, autoFocus]);

  return (
    // The card sets container-type: inline-size, so it has no width of its
    // own: its column gives it 320–400px, never wider than the screen. The
    // pane beside it shows on wide screens only (index.css).
    <div
      ref={wrapperRef}
      className='prompt-menu'
      data-cy='prompt-menu'
      onKeyDown={fillStep?.onKeyDown}
    >
      <div className='prompt-menu__card'>
        <CommandMenu
          groups={groups}
          onRun={id => onPick(id)}
          onActive={setActiveKey}
          query={query}
          filter={query === undefined}
          header={fillStep ? fillStep.header : header}
          panel={fillStep?.panel}
          controlRef={controlRef}
          askRow={false}
          hotkey={false}
          placeholder={placeholder}
          autoFocus={autoFocus}
          onClose={onClose}
          empty={<p className='prompt-menu__empty'>No prompts match</p>}
          footer={
            fillStep ? (
              fillStep.footer
            ) : (
              <>
                <span className='prompt-menu__hint'>
                  <span className='ui-cmdc__keys'>
                    <kbd>↑</kbd>
                    <kbd>↓</kbd>
                  </span>
                  to move
                </span>
                <span className='prompt-menu__hint'>
                  <kbd>↵</kbd>
                  {enterHint}
                  <kbd>esc</kbd>
                </span>
              </>
            )
          }
        />
      </div>
      {preview && <PreviewPane preview={preview} contextNames={contextNames} />}
    </div>
  );
}
